import { useEffect, useMemo } from "react";
import {
  UserRoles,
  UserRolesTranslations,
  UserStatus,
  UserStatusTranslations,
  UserWithDetails,
} from "../../types/user.types";
import { ErrorMessage } from "../../components/error-message";
import CrudTable from "../../components/crud-table";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useUsers } from "../../context/UsersContext";
import { getAvailableRoles } from "../../utils/available-roles.utils";
import { useAuth } from "../../context/AuthContext";
import { CrudTableHeader } from "../../components/crud-table-header";
import { useTableControls } from "../../hooks/useTableControls";
import { UserIcon } from "@heroicons/react/24/outline";
import { Loading } from "../../components/loading";

export function UsersCRUD() {
  const { users, isLoading, getUsers, error } = useUsers();
  const navigate = useNavigate();
  const location = useLocation();
  const isListView = location.pathname === "/dashboard/users";
  const { user } = useAuth();
  const availableRoles = getAvailableRoles(user?.roleId! as UserRoles);

  const {
    paginatedData: paginatedUsers,
    filteredData,
    totalPages,
    currentPage,
    sortConfig,
    setSortConfig,
    setSearchQuery,
    setSelectedFilters,
    setCurrentPage,
  } = useTableControls(users || []);

  const columns = useMemo(
    () => [
      {
        key: "id",
        label: "N° Usuario",
        sortable: true,
        render: (value: number) => value.toString(),
      },
      {
        key: "role_id",
        label: "Rol",
        sortable: true,
        render: (value: number) => UserRolesTranslations[value as UserRoles],
      },
      {
        key: "status",
        label: "Estado",
        sortable: true,
        render: (value: UserStatus) =>
          UserStatusTranslations[value as UserStatus],
      },
      {
        key: "username",
        label: "Usuario",
        sortable: true,
      },
      {
        key: "first_name",
        label: "Nombre",
        sortable: true,
        render: (value: string | null) => value || "-",
      },
      {
        key: "last_name",
        label: "Apellido",
        sortable: true,
        render: (value: string | null) => value || "-",
      },
      {
        key: "dni",
        label: "DNI",
        sortable: true,
        render: (value: string | null) => value || "-",
      },
      {
        key: "team_name",
        label: "Equipo",
        sortable: true,
        render: (value: string | null) => value || "-",
      },
    ],
    []
  );

  useEffect(() => {
    getUsers();
  }, [getUsers]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const handleSort = (
    sortBy: string | null,
    sortDirection: "asc" | "desc" | null
  ) => {
    setSortConfig({
      sortBy: sortBy as keyof UserWithDetails | null,
      sortDirection,
    });
  };
  const handleFilterChange = (selectedFilters: Record<string, string[]>) => {
    setSelectedFilters(selectedFilters);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="w-full p-8">
      {error && (
        <div className="mb-4">
          <ErrorMessage message={error} dismissible />
        </div>
      )}

      <div>
        <CrudTableHeader
          title="Usuarios"
          count={users?.length}
          icon={<UserIcon />}
        />

        {isListView &&
          (isLoading ? (
            <Loading />
          ) : paginatedUsers && paginatedUsers.length > 0 ? (
            <CrudTable
              data={paginatedUsers}
              columns={columns}
              onAdd={{
                onClick: () => navigate("/dashboard/users/new"),
                label: "Agregar usuario",
              }}
              search={{
                placeholder: "Buscar usuarios...",
                onSearch: handleSearch,
              }}
              sort={{
                sortBy: sortConfig.sortBy,
                sortDirection: sortConfig.sortDirection,
                onSort: handleSort,
              }}
              actions={{
                view: {
                  onClick: (user: UserWithDetails) =>
                    navigate(`/dashboard/users/view/${user.id}`),
                  label: "",
                },
              }}
              filters={[
                {
                  key: "role_id",
                  label: "Rol",
                  multiple: true,
                  options: availableRoles,
                },
                {
                  key: "status",
                  label: "Estado",
                  multiple: true,
                  options: Object.entries(UserStatusTranslations).map(
                    ([statusId, statusName]) => ({
                      key: String(statusId),
                      value: statusName,
                    })
                  ),
                },
              ]}
              pagination={{
                page: currentPage,
                totalPages: totalPages,
                onPageChange: handlePageChange,
                labels: {
                  next: "Siguiente",
                  previous: "Anterior",
                  pageInfo: `Página ${currentPage} de ${totalPages}`,
                },
              }}
              onFilterChange={handleFilterChange}
              totalCount={filteredData.length}
            />
          ) : null)}

        <Outlet />
      </div>
    </div>
  );
}
